/**
 * Invoices list screen (WO-M4).
 *
 * Lists InvoiceSummary rows: status, total, balance_due. Tapping a row
 * navigates to the invoice detail screen at /(app)/invoices/[id].
 *
 * invoiceId is an opaque UUID — no PII in URLs (mobile CLAUDE.md §2).
 *
 * WO-M0 refactor — rebuilt on the design system (Screen / ListRow / Badge / Text + tokens).
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { listInvoices } from '../../src/api/billing';
import { ApiRequestError } from '../../src/api/client';
import { Screen, Text, Badge, Card, Button, Field, ListRow, invoiceBadge } from '../../src/components/ui';
import { theme } from '../../src/theme';
import { filterInvoices } from '../../src/lib/listSearch';
import type { InvoiceSummary } from '@beam/api-types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: string | null | undefined, currency: string): string {
  if (!value) return '—';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  }).format(num);
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof ApiRequestError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Failed to load invoices.';
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export default function InvoicesScreen() {
  const router = useRouter();
  const [query, setQuery] = useState('');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => listInvoices(),
    staleTime: 30_000,
  });

  // Client-side filter of already-loaded rows (no extra fetch). Matches the
  // invoice number, VISIBLE short id (id.slice(0,8)) and status — see
  // src/lib/listSearch (searching the full UUID over-matched the hidden tail).
  const filtered = useMemo(() => (data ? filterInvoices(data, query) : data), [data, query]);

  if (isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading invoices"
          />
          <Text variant="body" color="secondary">
            Loading invoices…
          </Text>
        </View>
      </Screen>
    );
  }

  if (isError) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(error)}
            </Text>
            <Button
              title="Retry"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => refetch()}
              accessibilityLabel="Retry loading invoices"
            />
          </Card>
        </View>
      </Screen>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Text variant="title">No invoices yet</Text>
          <Text variant="body" color="secondary" style={styles.emptySubtitle}>
            Invoices are billed from an approved estimate or a job. Start one below.
          </Text>
          <Button
            title="+ New invoice"
            onPress={() => router.push('/(app)/invoices/new' as never)}
            accessibilityLabel="New invoice"
          />
        </View>
      </Screen>
    );
  }

  const rows = filtered ?? [];

  return (
    <Screen scroll>
      <Field
        placeholder="Search invoices"
        value={query}
        onChangeText={setQuery}
        autoCapitalize="none"
        autoCorrect={false}
        returnKeyType="search"
        clearButtonMode="while-editing"
        accessibilityLabel="Search invoices"
      />
      <Button
        title="+ New invoice"
        onPress={() => router.push('/(app)/invoices/new' as never)}
        accessibilityLabel="New invoice"
      />
      <Text variant="label" color="secondary">
        {rows.length} {rows.length === 1 ? 'invoice' : 'invoices'}
      </Text>
      {rows.length === 0 ? (
        <Text variant="body" color="secondary">
          No invoices match “{query.trim()}”.
        </Text>
      ) : null}
      <View style={styles.list}>
        {rows.map((invoice: InvoiceSummary) => {
          const b = invoiceBadge(invoice.status);
          const total = formatCurrency(invoice.total, invoice.currency);
          const balance = formatCurrency(invoice.balanceDue, invoice.currency);
          const hasDue = invoice.balanceDue != null && parseFloat(invoice.balanceDue) > 0;
          const dateStr = invoice.dueDate
            ? `Due ${new Date(invoice.dueDate).toLocaleDateString()}`
            : undefined;
          const subtitleParts = [dateStr, hasDue ? `Bal ${balance}` : undefined]
            .filter(Boolean)
            .join(' · ');
          return (
            <ListRow
              key={invoice.id}
              title={invoice.invoiceNumber ?? invoice.id.slice(0, 8).toUpperCase()}
              subtitle={subtitleParts || undefined}
              right={
                <View style={styles.rightSlot}>
                  <Text variant="data">{total}</Text>
                  <Badge status={b.key} label={b.label} />
                </View>
              }
              onPress={() => router.push(`/(app)/invoices/${invoice.id}`)}
            />
          );
        })}
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  emptySubtitle: {
    textAlign: 'center',
  },
  list: {
    gap: theme.spacing.sm,
  },
  rightSlot: {
    alignItems: 'flex-end',
    gap: theme.spacing.xs,
  },
});
